// Scénarios d'investissement proposés par budget (T5 ; « variantes » dans l'API) : les propositions de l'agent,
// chargées comme objets « travaux » et « décision », avec les chiffres RECALCULÉS par le moteur à chaque lecture
// (GET /v0/cartes/{id}/variantes). L'application n'en calcule aucun : elle les met en forme. Vocabulaire :
// docs/REGLE-EDITORIALE.md.
import { ReserveCalcul, ReserveCourte } from './ManquesCalcul';
import { useState } from 'react';
import type { IndicateursVariante, Variante, Variantes } from '../api/types';
import { decimal, eurosCourt, eurosLong, fourchette, pourcent } from '../domaine/format';
import { lienVers } from '../routeur';
import { PanneauEssai } from './EssaiVariante';

function nis2(i: IndicateursVariante | undefined | null): string {
  return i?.score_nis2 === undefined || i?.score_nis2 === null ? '—' : `${decimal(i.score_nis2)} / 10`;
}

function rayon(i: IndicateursVariante | undefined | null): string {
  return i?.rayon_max === undefined || i?.rayon_max === null ? '—' : pourcent(i.rayon_max);
}

function perteEvitee(v: Variante): string {
  const p = v.perte_evitee;
  return p?.bas && p?.haut ? fourchette(p.bas.valeur, p.haut.valeur) : '—';
}

export function PanneauVariantes({ variantes, nom }: { variantes: Variantes; nom?: (id: string) => string }) {
  const liste = variantes.variantes;
  const [essai, setEssai] = useState<string | null>(null);
  if (liste.length === 0 && !variantes.manques?.length) return null;
  const enEssai = liste.find((v) => v.id === essai && v.calculable);
  const avant = variantes.avant;
  const horizon = variantes.horizon ?? 3;
  return (
    <section className="panneau" aria-labelledby="t-variantes" data-testid="variantes">
      <div className="panneau-tete">
        <h2 id="t-variantes">Scénarios d’investissement proposés</h2>
        <span className="indice">À valider par l’entreprise · chiffres recalculés par le moteur sur la carte actuelle · horizon {horizon} ans</span>
      </div>
      <ReserveCalcul manques={variantes.manques} nom={nom} />
      {avant && (
        <p className="note">
          Aujourd’hui : indicateur de progression NIS2 <span className="num">{nis2(avant)}</span>, arrêt maximal <span className="num">{rayon(avant)}</span>
          {avant.perte_annuelle_attendue?.bas && avant.perte_annuelle_attendue?.haut && (
            <>
              , pertes potentielles estimées{' '}
              <span className="num">
                {fourchette(avant.perte_annuelle_attendue.bas.valeur, avant.perte_annuelle_attendue.haut.valeur)}
              </span>{' '}
              par an, conditionnelles aux hypothèses retenues (calibration provisoire, non validée par un actuaire)
            </>
          )}
          , {avant.sauvegarde_hors_ligne ? 'sauvegarde hors ligne' : 'aucune sauvegarde hors ligne'}.
        </p>
      )}
      <div className="tableau-defile">
        <table>
          <caption className="sr">Scénarios d’investissement proposés par budget, chiffrés par le moteur</caption>
          <thead>
            <tr>
              <th scope="col">Budget</th>
              <th scope="col" className="num">
                Coût sur {horizon} ans
              </th>
              <th scope="col" className="num">
                Pertes potentielles évitées sur {horizon} ans
              </th>
              <th scope="col" className="num">
                Indicateur NIS2 après
              </th>
              <th scope="col" className="num">
                Arrêt maximal après
              </th>
              <th scope="col">Sauvegarde hors ligne</th>
              <th scope="col">Fiche</th>
              <th scope="col">Essai</th>
            </tr>
          </thead>
          <tbody>
            {liste.map((v) => (
              <tr key={v.id} data-testid={`variante-${v.id}`}>
                <th scope="row">{v.budget ? eurosCourt(v.budget.valeur) : v.nom}
                  <ReserveCourte manques={variantes.manques} />
                  <ReserveCalcul manques={v.manques} nom={nom} />
                </th>
                {v.calculable ? (
                  <>
                    <td className="num">{v.cout?.total ? eurosLong(v.cout.total.valeur) : '—'}</td>
                    <td className="num">{perteEvitee(v)}</td>
                    <td className="num">{nis2(v.apres)}</td>
                    <td className="num">{rayon(v.apres)}</td>
                    <td>{v.apres?.sauvegarde_hors_ligne ? 'oui' : 'non'}</td>
                  </>
                ) : (
                  <td colSpan={5} className="note">
                    {(v.gestes_inconnus?.length ?? 0) > 0
                      ? `Non calculable sur la carte actuelle : ${v.gestes_inconnus?.length} geste(s) n’existent plus.`
                      : 'Non calculable : scénario mal formé (budget ou gestes illisibles).'}{' '}
                    Aucun chiffre n’est affiché.
                  </td>
                )}
                <td>{v.decision ? <a href={lienVers('decisions', { decision: v.decision })}>Préparée par Ekera</a> : '—'}</td>
                <td>
                  {v.calculable ? (
                    <button
                      type="button"
                      className="btn petit"
                      aria-pressed={essai === v.id}
                      onClick={() => setEssai(essai === v.id ? null : v.id)}
                      data-testid={`essayer-${v.id}`}
                    >
                      Essayer
                    </button>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {enEssai && <PanneauEssai key={enEssai.id} variante={enEssai} surFermer={() => setEssai(null)} />}
      {liste.map(
        (v) =>
          v.calculable &&
          (v.gestes?.length ?? 0) > 0 && (
            <details key={v.id}>
              <summary>
                {v.budget ? `Budget de ${eurosCourt(v.budget.valeur)}` : v.nom} : {v.gestes?.length} geste
                {(v.gestes?.length ?? 0) > 1 ? 's' : ''}
              </summary>
              <ReserveCalcul manques={[...(variantes.manques ?? []), ...(v.manques ?? [])]} nom={nom} />
              <ul className="liste-points" style={{ marginTop: 8 }}>
                {v.gestes?.map((g) => (
                  <li key={g.id}>
                    <b>{g.libelle ?? g.id}</b>
                    <small className="num">
                      mise en place {g.mise_en_place ? eurosLong(g.mise_en_place.valeur) : '—'}
                      {g.annuel && g.annuel.valeur > 0 ? ` · ${eurosLong(g.annuel.valeur)} par an` : ''}
                    </small>
                  </li>
                ))}
              </ul>
            </details>
          ),
      )}
      <p className="note">
        L’IA propose, l’humain décide : chaque scénario a sa fiche de décision pré-remplie, à valider ou à écarter. Un
        scénario n’est pas un chantier tant qu’il n’est pas retenu. « Essayer » recalcule un scénario modifié sans rien
        écrire.
      </p>
    </section>
  );
}
